/**
 * Heaven Environment — 7 Halls of the Angelic Orders
 *
 * Layout (top-down view):
 *
 *                    [Hall of Seraphim]
 *            [Cherubim]            [Thrones]
 *        [Dominions]                  [Virtues]
 *            [Powers]            [Principalities]
 *                    |  Grand Avenue  |
 *                    [  Pearly Gates  ]  ← spawn
 *
 * Each hall has unique architecture, lighting, particle effects, and audio zone.
 */

import * as THREE from 'three';

// ─── Color palettes per hall ──────────────────────────────────────
const HALL_THEMES = {
  seraphim:       { primary: 0xff4400, secondary: 0xffaa00, ambient: 0xff6633, fog: 0x331100, name: 'Hall of Seraphim',       subtitle: 'The Burning Ones — Closest to the Divine Light' },
  cherubim:       { primary: 0x2266ff, secondary: 0x88ccff, ambient: 0x4488ff, fog: 0x0a1133, name: 'Hall of Cherubim',       subtitle: 'Keepers of Wisdom & Sacred Knowledge' },
  thrones:        { primary: 0x9933ff, secondary: 0xcc88ff, ambient: 0x7744cc, fog: 0x1a0033, name: 'Hall of Thrones',        subtitle: 'Bearers of Divine Justice & Authority' },
  dominions:      { primary: 0xffcc00, secondary: 0xffeebb, ambient: 0xddaa22, fog: 0x332200, name: 'Hall of Dominions',      subtitle: 'Governors of the Celestial Order' },
  virtues:        { primary: 0x00cc66, secondary: 0x88ffbb, ambient: 0x33aa77, fog: 0x002211, name: 'Hall of Virtues',        subtitle: 'Channels of Miracles & Natural Wonder' },
  powers:         { primary: 0xcc0033, secondary: 0xff6688, ambient: 0xaa2244, fog: 0x220011, name: 'Hall of Powers',         subtitle: 'Guardians Against Darkness' },
  principalities: { primary: 0xffffff, secondary: 0xeeeeff, ambient: 0xccccee, fog: 0x222233, name: 'Hall of Principalities', subtitle: 'Watchers Over Nations & Peoples' },
};

// Hall positions in a semicircle arrangement
const HALL_POSITIONS = {
  seraphim:       { x: 0,   z: -120 },  // top center (furthest, most sacred)
  cherubim:       { x: -50, z: -105 },
  thrones:        { x: 50,  z: -105 },
  dominions:      { x: -75, z: -75 },
  virtues:        { x: 75,  z: -75 },
  powers:         { x: -50, z: -50 },
  principalities: { x: 50,  z: -50 },
};

// ─── Exported class ───────────────────────────────────────────────
export class HeavenEnvironment {
  constructor(scene) {
    this.scene = scene;
    this.groups = {};
    this.particles = [];
    this.lights = [];
    this.audioZones = [];  // { position, radius, hallName }
    this.floatingObjects = [];
    this.waterPlanes = [];
    this.time = 0;

    this.build();
  }

  build() {
    this._buildSkyAndAtmosphere();
    this._buildTerrain();
    this._buildPearlyGates();
    this._buildGrandAvenue();

    // Build each hall
    for (const [key, theme] of Object.entries(HALL_THEMES)) {
      const pos = HALL_POSITIONS[key];
      this._buildHall(key, theme, pos.x, pos.z);
      this._buildPathToHall(pos.x, pos.z);
    }

    this._buildGlobalParticles();
  }

  // ─── SKY & ATMOSPHERE ──────────────────────────────────────────
  _buildSkyAndAtmosphere() {
    // Gradient sky dome
    const skyGeo = new THREE.SphereGeometry(300, 32, 32);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      uniforms: {
        topColor:    { value: new THREE.Color(0xfff8e7) },
        bottomColor: { value: new THREE.Color(0xd4e8ed) },
        offset:      { value: 20 },
        exponent:    { value: 0.4 },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(sky);

    // Warm heavenly fog
    this.scene.fog = new THREE.FogExp2(0xf0e8d8, 0.004);

    // Warm ambient light
    const ambient = new THREE.AmbientLight(0xfff5e0, 0.5);
    this.scene.add(ambient);

    // Main sun / divine light from above
    const sunLight = new THREE.DirectionalLight(0xfffaf0, 1.2);
    sunLight.position.set(0, 150, -60);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 2048;
    sunLight.shadow.mapSize.height = 2048;
    sunLight.shadow.camera.near = 1;
    sunLight.shadow.camera.far = 400;
    sunLight.shadow.camera.left = -150;
    sunLight.shadow.camera.right = 150;
    sunLight.shadow.camera.top = 150;
    sunLight.shadow.camera.bottom = -150;
    this.scene.add(sunLight);

    // Soft hemisphere light (sky + ground bounce)
    const hemi = new THREE.HemisphereLight(0xfff8e0, 0xd4c8a0, 0.4);
    this.scene.add(hemi);
  }

  // ─── TERRAIN ───────────────────────────────────────────────────
  _buildTerrain() {
    // Procedural marble texture for realistic heavenly ground
    const marbleCanvas = document.createElement('canvas');
    marbleCanvas.width = 512;
    marbleCanvas.height = 512;
    const mCtx = marbleCanvas.getContext('2d');

    // Base warm white
    mCtx.fillStyle = '#f5ede0';
    mCtx.fillRect(0, 0, 512, 512);

    // Marble veins
    mCtx.globalAlpha = 0.06;
    for (let i = 0; i < 80; i++) {
      mCtx.strokeStyle = i % 3 === 0 ? '#c8b090' : '#e0d5c5';
      mCtx.lineWidth = Math.random() * 2 + 0.5;
      mCtx.beginPath();
      let x = Math.random() * 512;
      let y = Math.random() * 512;
      mCtx.moveTo(x, y);
      for (let j = 0; j < 8; j++) {
        x += (Math.random() - 0.5) * 100;
        y += (Math.random() - 0.5) * 100;
        mCtx.lineTo(x, y);
      }
      mCtx.stroke();
    }
    mCtx.globalAlpha = 1;

    const marbleTex = new THREE.CanvasTexture(marbleCanvas);
    marbleTex.wrapS = THREE.RepeatWrapping;
    marbleTex.wrapT = THREE.RepeatWrapping;
    marbleTex.repeat.set(20, 16);

    // Generate a normal map for depth
    const normalCanvas = document.createElement('canvas');
    normalCanvas.width = 256;
    normalCanvas.height = 256;
    const nCtx = normalCanvas.getContext('2d');
    nCtx.fillStyle = '#8080ff'; // flat normal
    nCtx.fillRect(0, 0, 256, 256);
    // Subtle grain
    for (let i = 0; i < 2000; i++) {
      const shade = 120 + Math.random() * 16;
      nCtx.fillStyle = `rgb(${shade}, ${shade}, 255)`;
      nCtx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
    }
    const normalTex = new THREE.CanvasTexture(normalCanvas);
    normalTex.wrapS = THREE.RepeatWrapping;
    normalTex.wrapT = THREE.RepeatWrapping;
    normalTex.repeat.set(20, 16);

    const groundGeo = new THREE.PlaneGeometry(500, 400, 40, 40);

    // Gentle rolling hills
    const posAttr = groundGeo.attributes.position;
    for (let i = 0; i < posAttr.count; i++) {
      const x = posAttr.getX(i);
      const y = posAttr.getY(i);
      const z = Math.sin(x * 0.02) * Math.cos(y * 0.02) * 1.2
              + Math.sin(x * 0.05 + y * 0.03) * 0.4;
      posAttr.setZ(i, z);
    }
    groundGeo.computeVertexNormals();

    const groundMat = new THREE.MeshStandardMaterial({
      map: marbleTex,
      normalMap: normalTex,
      normalScale: new THREE.Vector2(0.3, 0.3),
      metalness: 0.15,
      roughness: 0.45,
      envMapIntensity: 0.5,
    });

    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.1;
    ground.receiveShadow = true;
    ground.userData.isGround = true;
    this.scene.add(ground);

    // Add subtle cloud shadows moving across the ground
    const cloudShadowCanvas = document.createElement('canvas');
    cloudShadowCanvas.width = 256;
    cloudShadowCanvas.height = 256;
    const cCtx = cloudShadowCanvas.getContext('2d');
    cCtx.fillStyle = 'rgba(0,0,0,0)';
    cCtx.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 15; i++) {
      const grad = cCtx.createRadialGradient(
        Math.random() * 256, Math.random() * 256, 0,
        Math.random() * 256, Math.random() * 256, 40 + Math.random() * 60
      );
      grad.addColorStop(0, 'rgba(255,248,230,0.12)');
      grad.addColorStop(1, 'rgba(255,248,230,0)');
      cCtx.fillStyle = grad;
      cCtx.fillRect(0, 0, 256, 256);
    }
    const cloudTex = new THREE.CanvasTexture(cloudShadowCanvas);
    cloudTex.wrapS = THREE.RepeatWrapping;
    cloudTex.wrapT = THREE.RepeatWrapping;

    const cloudPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(300, 300),
      new THREE.MeshBasicMaterial({ map: cloudTex, transparent: true, opacity: 0.3, depthWrite: false })
    );
    cloudPlane.rotation.x = -Math.PI / 2;
    cloudPlane.position.y = 0.02;
    this.scene.add(cloudPlane);
    this._cloudPlane = cloudPlane;
  }

  // ─── PEARLY GATES ─────────────────────────────────────────────
  _buildPearlyGates() {
    const group = new THREE.Group();
    group.position.set(0, 0, 5);

    const pearlMat = new THREE.MeshStandardMaterial({
      color: 0xfff8f0,
      metalness: 0.3,
      roughness: 0.2,
      emissive: 0xfff0d0,
      emissiveIntensity: 0.15,
    });

    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      metalness: 0.8,
      roughness: 0.2,
      emissive: 0xffaa00,
      emissiveIntensity: 0.2,
    });

    // Two massive gate pillars
    const pillarGeo = new THREE.CylinderGeometry(1.5, 1.8, 20, 16);
    for (let side = -1; side <= 1; side += 2) {
      const pillar = new THREE.Mesh(pillarGeo, pearlMat);
      pillar.position.set(side * 8, 10, 0);
      pillar.castShadow = true;
      group.add(pillar);

      // Pillar cap (gold)
      const capGeo = new THREE.SphereGeometry(2, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
      const cap = new THREE.Mesh(capGeo, goldMat);
      cap.position.set(side * 8, 20, 0);
      group.add(cap);

      // Pillar base
      const baseGeo = new THREE.CylinderGeometry(2.2, 2.5, 1.5, 16);
      const base = new THREE.Mesh(baseGeo, goldMat);
      base.position.set(side * 8, 0.75, 0);
      group.add(base);
    }

    // Grand arch spanning the pillars
    const archShape = new THREE.Shape();
    const archRadius = 8;
    archShape.absarc(0, 0, archRadius, 0, Math.PI, false);
    const extrudeSettings = { depth: 1.5, bevelEnabled: true, bevelThickness: 0.3, bevelSize: 0.2, bevelSegments: 4 };
    const archGeo = new THREE.ExtrudeGeometry(archShape, extrudeSettings);
    const arch = new THREE.Mesh(archGeo, goldMat);
    arch.position.set(0, 20, -0.75);
    arch.castShadow = true;
    group.add(arch);

    // "VERIDIAN" text placeholder — golden banner
    const bannerGeo = new THREE.PlaneGeometry(10, 2);
    const bannerCanvas = document.createElement('canvas');
    bannerCanvas.width = 512;
    bannerCanvas.height = 128;
    const ctx = bannerCanvas.getContext('2d');
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.fillRect(0, 0, 512, 128);
    ctx.font = 'bold 60px serif';
    ctx.fillStyle = '#ffd700';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('VERIDIAN', 256, 50);
    ctx.font = '28px serif';
    ctx.fillStyle = '#fff8e0';
    ctx.fillText('The Seven Halls of Heaven', 256, 100);
    const bannerTex = new THREE.CanvasTexture(bannerCanvas);
    const bannerMat = new THREE.MeshBasicMaterial({ map: bannerTex, transparent: true, side: THREE.DoubleSide });
    const banner = new THREE.Mesh(bannerGeo, bannerMat);
    banner.position.set(0, 25, 0);
    group.add(banner);

    // Gate doors (two panels, slightly open)
    const doorGeo = new THREE.BoxGeometry(7, 18, 0.4);
    const doorMat = new THREE.MeshStandardMaterial({
      color: 0xfff0d0,
      metalness: 0.6,
      roughness: 0.15,
      emissive: 0xffd700,
      emissiveIntensity: 0.1,
    });

    const leftDoor = new THREE.Mesh(doorGeo, doorMat);
    leftDoor.position.set(-4, 9, 0.8);
    leftDoor.rotation.y = 0.3;
    leftDoor.castShadow = true;
    group.add(leftDoor);

    const rightDoor = new THREE.Mesh(doorGeo, doorMat);
    rightDoor.position.set(4, 9, 0.8);
    rightDoor.rotation.y = -0.3;
    rightDoor.castShadow = true;
    group.add(rightDoor);

    // Gold cross bars on doors
    for (const door of [leftDoor, rightDoor]) {
      for (let row = 0; row < 3; row++) {
        const barGeo = new THREE.BoxGeometry(6.5, 0.15, 0.5);
        const bar = new THREE.Mesh(barGeo, goldMat);
        bar.position.set(0, -6 + row * 6, 0.1);
        door.add(bar);
      }
      const vBarGeo = new THREE.BoxGeometry(0.15, 17.5, 0.5);
      const vBar = new THREE.Mesh(vBarGeo, goldMat);
      vBar.position.set(0, 0, 0.1);
      door.add(vBar);
    }

    // Glowing light between gates
    const gateLight = new THREE.PointLight(0xfff0c0, 2, 30, 1);
    gateLight.position.set(0, 12, 1);
    group.add(gateLight);
    this.lights.push({ light: gateLight, baseIntensity: 2, pulse: true });

    // Steps leading up to gates
    for (let i = 0; i < 5; i++) {
      const stepGeo = new THREE.BoxGeometry(20 - i * 0.5, 0.4, 2);
      const step = new THREE.Mesh(stepGeo, pearlMat);
      step.position.set(0, i * 0.4, 3 + i * 2);
      step.receiveShadow = true;
      group.add(step);
    }

    this.scene.add(group);
    this.groups.pearlyGates = group;
  }

  // ─── GRAND AVENUE ─────────────────────────────────────────────
  _buildGrandAvenue() {
    const group = new THREE.Group();

    const goldPathMat = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      metalness: 0.7,
      roughness: 0.15,
      emissive: 0xffaa00,
      emissiveIntensity: 0.08,
    });

    // Main golden road from gates into the realm
    const roadGeo = new THREE.PlaneGeometry(10, 130);
    const road = new THREE.Mesh(roadGeo, goldPathMat);
    road.rotation.x = -Math.PI / 2;
    road.position.set(0, 0.05, -60);
    road.receiveShadow = true;
    group.add(road);

    // Decorative border lines
    const borderMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.4,
      roughness: 0.3,
      emissive: 0xfff0d0,
      emissiveIntensity: 0.1,
    });

    for (let side = -1; side <= 1; side += 2) {
      const borderGeo = new THREE.BoxGeometry(0.3, 0.3, 130);
      const border = new THREE.Mesh(borderGeo, borderMat);
      border.position.set(side * 5.2, 0.15, -60);
      group.add(border);

      // Lamp posts along the avenue
      for (let z = 0; z > -125; z -= 15) {
        this._addLampPost(group, side * 7, z);
      }
    }

    // Decorative fountain in the center
    this._buildFountain(group, 0, -25);

    this.scene.add(group);
    this.groups.grandAvenue = group;
  }

  _addLampPost(parent, x, z) {
    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, metalness: 0.7, roughness: 0.2,
    });

    const poleGeo = new THREE.CylinderGeometry(0.1, 0.15, 6, 8);
    const pole = new THREE.Mesh(poleGeo, goldMat);
    pole.position.set(x, 3, z);
    pole.castShadow = true;
    parent.add(pole);

    const orbGeo = new THREE.SphereGeometry(0.4, 16, 16);
    const orbMat = new THREE.MeshStandardMaterial({
      color: 0xfff8e0, emissive: 0xfff0c0, emissiveIntensity: 0.8, transparent: true, opacity: 0.9,
    });
    const orb = new THREE.Mesh(orbGeo, orbMat);
    orb.position.set(x, 6.3, z);
    parent.add(orb);

    const light = new THREE.PointLight(0xfff0c0, 0.8, 15, 1.5);
    light.position.set(x, 6.5, z);
    parent.add(light);
  }

  _buildFountain(parent, x, z) {
    const marbleMat = new THREE.MeshStandardMaterial({
      color: 0xfff8f0, metalness: 0.15, roughness: 0.3,
    });

    // Basin
    const basinGeo = new THREE.CylinderGeometry(4, 4.5, 1.2, 24);
    const basin = new THREE.Mesh(basinGeo, marbleMat);
    basin.position.set(x, 0.6, z);
    basin.receiveShadow = true;
    parent.add(basin);

    // Water surface
    const waterGeo = new THREE.CircleGeometry(3.8, 24);
    const waterMat = new THREE.MeshStandardMaterial({
      color: 0x88ccff, metalness: 0.3, roughness: 0.1, transparent: true, opacity: 0.6,
    });
    const water = new THREE.Mesh(waterGeo, waterMat);
    water.rotation.x = -Math.PI / 2;
    water.position.set(x, 1.1, z);
    parent.add(water);
    this.waterPlanes.push(water);

    // Central pillar
    const pillarGeo = new THREE.CylinderGeometry(0.4, 0.5, 3, 12);
    const pillar = new THREE.Mesh(pillarGeo, marbleMat);
    pillar.position.set(x, 2.5, z);
    parent.add(pillar);

    // Top angel figure (simple stylized)
    const angelGeo = new THREE.ConeGeometry(0.6, 1.5, 8);
    const angelMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, metalness: 0.6, roughness: 0.2, emissive: 0xffaa00, emissiveIntensity: 0.15,
    });
    const angel = new THREE.Mesh(angelGeo, angelMat);
    angel.position.set(x, 4.5, z);
    parent.add(angel);
    this.floatingObjects.push({ mesh: angel, baseY: 4.5, amplitude: 0.15, speed: 1.2 });

    // Light above fountain
    const fLight = new THREE.PointLight(0xaaddff, 1.2, 20);
    fLight.position.set(x, 5, z);
    parent.add(fLight);
  }

  // ─── PATH TO EACH HALL ─────────────────────────────────────────
  _buildPathToHall(targetX, targetZ) {
    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, metalness: 0.6, roughness: 0.2, emissive: 0xffaa00, emissiveIntensity: 0.05,
    });

    // Calculate path from grand avenue to hall
    const startX = 0;
    const startZ = Math.max(targetZ, -120);

    const dx = targetX - startX;
    const dz = targetZ - startZ;
    const length = Math.sqrt(dx * dx + dz * dz);
    const angle = Math.atan2(dx, dz);

    if (length < 5) return; // Too close, skip

    const pathGeo = new THREE.PlaneGeometry(4, length);
    const path = new THREE.Mesh(pathGeo, goldMat);
    path.rotation.x = -Math.PI / 2;
    path.rotation.z = -angle;
    path.position.set((startX + targetX) / 2, 0.04, (startZ + targetZ) / 2);
    path.receiveShadow = true;
    this.scene.add(path);
  }

  // ─── BUILD A HALL ──────────────────────────────────────────────
  _buildHall(key, theme, centerX, centerZ) {
    const group = new THREE.Group();
    group.position.set(centerX, 0, centerZ);

    const hallSize = 28;  // diameter
    const wallHeight = 16;
    const numColumns = 8; // Reduced from 12 for performance

    // ─── Materials ───
    const marbleMat = new THREE.MeshStandardMaterial({
      color: 0xfff8f0, metalness: 0.15, roughness: 0.35,
    });

    const accentMat = new THREE.MeshStandardMaterial({
      color: theme.primary, metalness: 0.5, roughness: 0.25,
      emissive: theme.primary, emissiveIntensity: 0.2,
    });

    const glowMat = new THREE.MeshStandardMaterial({
      color: theme.secondary, emissive: theme.secondary, emissiveIntensity: 0.5,
      transparent: true, opacity: 0.7,
    });

    // ─── Circular floor ───
    const floorGeo = new THREE.CircleGeometry(hallSize / 2, 32);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0xfff5e8, metalness: 0.25, roughness: 0.2,
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0.08;
    floor.receiveShadow = true;
    group.add(floor);

    // ─── Inner design ring on the floor ───
    const ringGeo = new THREE.RingGeometry(hallSize / 2 - 3, hallSize / 2 - 2.5, 32);
    const ringMesh = new THREE.Mesh(ringGeo, accentMat);
    ringMesh.rotation.x = -Math.PI / 2;
    ringMesh.position.y = 0.1;
    group.add(ringMesh);

    // ─── Center emblem ───
    const emblemGeo = new THREE.CircleGeometry(2.5, 6); // hexagonal
    const emblem = new THREE.Mesh(emblemGeo, accentMat);
    emblem.rotation.x = -Math.PI / 2;
    emblem.position.y = 0.12;
    group.add(emblem);

    // ─── Columns ───
    for (let i = 0; i < numColumns; i++) {
      const angle = (i / numColumns) * Math.PI * 2;
      const colX = Math.cos(angle) * (hallSize / 2 - 1);
      const colZ = Math.sin(angle) * (hallSize / 2 - 1);

      // Column shaft
      const colGeo = new THREE.CylinderGeometry(0.5, 0.6, wallHeight, 12);
      const col = new THREE.Mesh(colGeo, marbleMat);
      col.position.set(colX, wallHeight / 2, colZ);
      col.castShadow = true;
      group.add(col);

      // Column base
      const baseGeo = new THREE.CylinderGeometry(0.8, 0.9, 0.6, 12);
      const base = new THREE.Mesh(baseGeo, accentMat);
      base.position.set(colX, 0.3, colZ);
      group.add(base);

      // Column capital (top)
      const capitalGeo = new THREE.CylinderGeometry(0.9, 0.5, 0.8, 12);
      const capital = new THREE.Mesh(capitalGeo, accentMat);
      capital.position.set(colX, wallHeight, colZ);
      group.add(capital);
    }

    // ─── Dome / ceiling ───
    const domeGeo = new THREE.SphereGeometry(hallSize / 2, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    const domeMat = new THREE.MeshStandardMaterial({
      color: theme.secondary,
      metalness: 0.3,
      roughness: 0.4,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.4,
    });
    const dome = new THREE.Mesh(domeGeo, domeMat);
    dome.position.y = wallHeight;
    group.add(dome);

    // ─── Dome oculus (hole at top with light beam) ───
    const oculusGeo = new THREE.CylinderGeometry(2, 2, 1, 16, 1, true);
    const oculusMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.6,
      transparent: true, opacity: 0.3, side: THREE.DoubleSide,
    });
    const oculus = new THREE.Mesh(oculusGeo, oculusMat);
    oculus.position.y = wallHeight + hallSize / 2 - 1;
    group.add(oculus);

    // Light beam from oculus
    const beamGeo = new THREE.CylinderGeometry(0.3, 2.5, wallHeight + 5, 16, 1, true);
    const beamMat = new THREE.MeshBasicMaterial({
      color: theme.secondary, transparent: true, opacity: 0.06, side: THREE.DoubleSide,
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.y = (wallHeight + 5) / 2;
    group.add(beam);

    // ─── Central altar / throne ───
    const altarGeo = new THREE.BoxGeometry(3, 2, 3);
    const altarMat = new THREE.MeshStandardMaterial({
      color: theme.primary, metalness: 0.6, roughness: 0.15, emissive: theme.primary, emissiveIntensity: 0.15,
    });
    const altar = new THREE.Mesh(altarGeo, altarMat);
    altar.position.y = 1;
    altar.castShadow = true;
    group.add(altar);

    // Floating symbol above altar
    const symbolGeo = this._getHallSymbol(key);
    const symbol = new THREE.Mesh(symbolGeo, glowMat);
    symbol.position.y = 5;
    symbol.castShadow = true;
    group.add(symbol);
    this.floatingObjects.push({ mesh: symbol, baseY: 5, amplitude: 0.4, speed: 0.8, rotate: true, group });

    // ─── Hall name sign ───
    const signCanvas = document.createElement('canvas');
    signCanvas.width = 512;
    signCanvas.height = 128;
    const sCtx = signCanvas.getContext('2d');
    sCtx.clearRect(0, 0, 512, 128);
    sCtx.font = 'bold 40px serif';
    sCtx.fillStyle = '#ffd700';
    sCtx.textAlign = 'center';
    sCtx.textBaseline = 'middle';
    sCtx.fillText(theme.name, 256, 40);
    sCtx.font = '20px serif';
    sCtx.fillStyle = '#fff8e0';
    sCtx.fillText(theme.subtitle, 256, 90);

    const signTex = new THREE.CanvasTexture(signCanvas);
    const signMat = new THREE.MeshBasicMaterial({ map: signTex, transparent: true, side: THREE.DoubleSide });
    const signGeo = new THREE.PlaneGeometry(8, 2);
    const sign = new THREE.Mesh(signGeo, signMat);
    sign.position.set(0, wallHeight + 2, hallSize / 2 - 1);
    sign.rotation.y = Math.PI; // face inward toward entrance
    group.add(sign);

    // ─── Accent lighting ───
    const mainLight = new THREE.PointLight(theme.primary, 1.5, hallSize * 1.5, 1);
    mainLight.position.set(0, wallHeight - 2, 0);
    mainLight.castShadow = true;
    group.add(mainLight);
    this.lights.push({ light: mainLight, baseIntensity: 1.5, pulse: true, speed: 0.5 + Math.random() * 0.5 });

    // Accent up-lights at columns (every 3rd column)
    for (let i = 0; i < numColumns; i += 3) {
      const angle = (i / numColumns) * Math.PI * 2;
      const lx = Math.cos(angle) * (hallSize / 2 - 2);
      const lz = Math.sin(angle) * (hallSize / 2 - 2);
      const uLight = new THREE.PointLight(theme.secondary, 0.6, 12, 1.5);
      uLight.position.set(lx, 1, lz);
      group.add(uLight);
    }

    // ─── Hall-specific particles ───
    this._addHallParticles(group, key, theme, hallSize);

    // ─── Audio zone marker ───
    this.audioZones.push({
      position: new THREE.Vector3(centerX, 0, centerZ),
      radius: hallSize / 2 + 5,
      hallName: key,
      theme,
    });

    this.scene.add(group);
    this.groups[key] = group;
  }

  // ─── Hall-specific floating symbol ─────────────────────────────
  _getHallSymbol(key) {
    switch (key) {
      case 'seraphim':
        // Six-pointed star (fire)
        return new THREE.OctahedronGeometry(1.2, 0);
      case 'cherubim':
        // Book of wisdom — box
        return new THREE.BoxGeometry(1.5, 0.3, 1.2);
      case 'thrones':
        // Crown
        return new THREE.TorusGeometry(1, 0.3, 8, 6);
      case 'dominions':
        // Orb of authority
        return new THREE.SphereGeometry(0.9, 16, 16);
      case 'virtues':
        // Crystal (nature)
        return new THREE.IcosahedronGeometry(1, 0);
      case 'powers':
        // Shield — flattened cylinder
        return new THREE.CylinderGeometry(1, 1, 0.2, 6);
      case 'principalities':
        // Star / beacon
        return new THREE.TetrahedronGeometry(1.1, 0);
      default:
        return new THREE.SphereGeometry(0.8, 16, 16);
    }
  }

  // ─── Particles per hall (optimized — fewer particles, no per-frame vertex update) ───
  _addHallParticles(group, key, theme, hallSize) {
    const count = 80; // Reduced from 200
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const color = new THREE.Color(theme.secondary);

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * hallSize / 2;
      positions[i * 3]     = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.random() * 18;
      positions[i * 3 + 2] = Math.sin(angle) * radius;

      colors[i * 3]     = color.r * (0.7 + Math.random() * 0.3);
      colors[i * 3 + 1] = color.g * (0.7 + Math.random() * 0.3);
      colors[i * 3 + 2] = color.b * (0.7 + Math.random() * 0.3);
    }

    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const particleMat = new THREE.PointsMaterial({
      size: 0.18,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const particles = new THREE.Points(particleGeo, particleMat);
    group.add(particles);
    // Use group rotation only — no per-vertex updates
    this.particles.push({ points: particles, speed: 0.3 + Math.random() * 0.3, hallSize, rotateOnly: true });
  }

  // ─── Global atmosphere particles (golden dust — optimized) ─────
  _buildGlobalParticles() {
    const count = 500; // Reduced from 1500
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 300;
      positions[i * 3 + 1] = Math.random() * 40 + 1;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 300;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const mat = new THREE.PointsMaterial({
      size: 0.15,
      color: 0xfff0c0,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const globalParticles = new THREE.Points(geo, mat);
    this.scene.add(globalParticles);
    // Global particles just rotate slowly — no vertex updates
    this.particles.push({ points: globalParticles, speed: 0.1, global: true, rotateOnly: true });
  }

  // ─── ANIMATION UPDATE (call each frame) ────────────────────────
  update(time) {
    this.time = time;

    // Animate floating objects
    for (const obj of this.floatingObjects) {
      const worldY = obj.baseY + Math.sin(time * obj.speed) * obj.amplitude;
      obj.mesh.position.y = worldY;
      if (obj.rotate) {
        obj.mesh.rotation.y = time * 0.5;
        obj.mesh.rotation.x = Math.sin(time * 0.3) * 0.2;
      }
    }

    // Pulse lights
    for (const { light, baseIntensity, pulse, speed } of this.lights) {
      if (pulse) {
        light.intensity = baseIntensity + Math.sin(time * (speed || 1)) * baseIntensity * 0.2;
      }
    }

    // Animate particles cheaply — rotate the whole system instead of updating every vertex
    for (const p of this.particles) {
      p.points.rotation.y += p.speed * 0.002;
      if (!p.global) {
        p.points.position.y = Math.sin(time * p.speed * 0.5) * 0.3;
      }
    }

    // Animate water surfaces
    for (const water of this.waterPlanes) {
      water.material.opacity = 0.5 + Math.sin(time * 2) * 0.1;
    }

    // Slowly drift cloud shadows
    if (this._cloudPlane) {
      this._cloudPlane.material.map.offset.x = time * 0.003;
      this._cloudPlane.material.map.offset.y = time * 0.001;
    }
  }

  // ─── Get the audio zone the player is currently in ─────────────
  getCurrentAudioZone(playerPosition) {
    for (const zone of this.audioZones) {
      const dist = playerPosition.distanceTo(zone.position);
      if (dist < zone.radius) {
        return zone;
      }
    }
    return null; // In the open / grand avenue
  }

  // ─── Get the hall name for HUD display ─────────────────────────
  getCurrentLocation(playerPosition) {
    const zone = this.getCurrentAudioZone(playerPosition);
    if (zone) return zone.theme.name;

    // Check if near pearly gates
    if (playerPosition.z > -5) return 'The Pearly Gates';
    if (playerPosition.z > -35) return 'Grand Avenue';
    return 'The Celestial Fields';
  }
}
